import React from 'react';
import moment from 'moment-timezone';
import { getTableColumn, TableColumn, StatusBadge, Select, TextTooltip } from '@shared/UI';
import { IServiceTransfersDto } from '@shared/api';
import { usePaymentRequestsTable } from './PaymentRequestsTableProvider';
import { FilterArrowsIcon } from '@shared/icons/FilterArrowsIcon';
import { IconType } from 'react-icons';
import { getHeaderClassName } from '@shared/UI/SimpleTable/lib/getHeaderClassName';
import { HeaderCellWithFilter } from '@shared/UI/SimpleTable/components/HeaderCellWithFilter';
import { useLookups } from '@shared/providers/LookupsProvider';
import { mapLookupsToSelectOptions, formats } from '@shared/utils';

const greenStyles = {
    styles: {
        borderColor: '#CEF5E6',
        backgroundColor: '#ECFDF6',
    },
    textStyles: {
        color: '#047857',
    },
};

const redStyles = {
    styles: {
        borderColor: '#FEE2E2',
        backgroundColor: '#FCF1F1',
    },
    textStyles: {
        color: '#B91C1C',
    },
};

const blueStyles = {
    styles: {
        borderColor: '#CEE4F5',
        backgroundColor: '#E8F4FD',
    },
    textStyles: {
        color: '#0066A6',
    },
};

const STATUSES = {
    Pending: {
        name: 'Pending',
        ...blueStyles,
    },
    PendingPrinting: {
        name: 'Pending Printing',
        ...blueStyles,
    },
    Void: {
        name: 'Void',
        ...blueStyles,
    },
    FundsHeld: {
        name: 'Funds Held',
        ...blueStyles,
    },
    CompleteRestricted: {
        name: 'Complete Restricted',
        ...blueStyles,
    },
    Submitted: {
        name: 'Submitted',
        ...greenStyles,
    },
    Approved: {
        name: 'Approved',
        ...greenStyles,
    },
    FundsPosted: {
        name: 'Funds Posted',
        ...greenStyles,
    },
    SentToBank: {
        name: 'Sent To Bank',
        ...greenStyles,
    },
    Complete: {
        name: 'Complete',
        ...greenStyles,
    },
    Rejected: {
        name: 'Rejected',
        ...redStyles,
    },
    Canceled: {
        name: 'Canceled',
        ...redStyles,
    },
    Returned: {
        name: 'Returned',
        ...redStyles,
    },
    StopPayment: {
        name: 'StopPayment',
        ...redStyles,
    },
};

export function getPaymentRequestsTableColumns(): TableColumn<IServiceTransfersDto>[] {
    const { lookups } = useLookups();
    const transferMethodOptions = lookups && mapLookupsToSelectOptions('TransferMethod', lookups);
    const statusOptions = lookups && mapLookupsToSelectOptions('TransferStatus', lookups);
    const {
        desc,
        sortFieldName,
        paymentRequestsType,
        paymentRequestsMethod,
        setPaymentRequestsType,
        setPaymentRequestsMethod,
        handlePaymentRequestsSort,
    } = usePaymentRequestsTable();

    return [
        getTableColumn({
            dataField: 'accountNumber',
            title: 'Account Number',
            firstColumn: true,
            formatter: (data) => (
                <TextTooltip
                    text={String(data)}
                    maxLength={10}
                />
            ),
            headerFormatter: (row) => (
                <HeaderCellWithFilter
                    title={String(row)}
                    icon={FilterArrowsIcon as IconType}
                    onClick={() => handlePaymentRequestsSort('accountNumber')}
                />
            ),
            headerClassName: `payment-requests-table-header ${getHeaderClassName(
                sortFieldName,
                desc,
                'accountNumber',
            )}`,
        }),
        getTableColumn({
            dataField: 'createdAt',
            title: 'Created On',
            formatter: (data) => moment(String(data)).format(formats.fullTime),
            headerFormatter: (row) => (
                <HeaderCellWithFilter
                    title={String(row)}
                    icon={FilterArrowsIcon as IconType}
                    onClick={() => handlePaymentRequestsSort('createdAt')}
                />
            ),
            headerClassName: `payment-requests-table-header ${getHeaderClassName(sortFieldName, desc, 'createdAt')}`,
        }),
        getTableColumn({
            dataField: 'settlementDate',
            formatter: (data) => (data ? moment(String(data)).format(formats.fullTime) : '-'),
        }),
        getTableColumn({
            dataField: 'amount',
            headerFormatter: (row) => (
                <HeaderCellWithFilter
                    title={String(row)}
                    icon={FilterArrowsIcon as IconType}
                    onClick={() => handlePaymentRequestsSort('amount')}
                />
            ),
            headerClassName: `payment-requests-table-header ${getHeaderClassName(sortFieldName, desc, 'amount')}`,
        }),
        getTableColumn({
            dataField: 'method',
            title: 'Network',
            headerFormatter: () => (
                <Select
                    currentValue={paymentRequestsMethod}
                    options={transferMethodOptions || []}
                    dropdownPlacement='bottom-right'
                    placeholder='Network'
                    multiple
                    nonInputView
                    withClearButton
                    onSelect={setPaymentRequestsMethod}
                />
            ),
        }),
        getTableColumn({
            dataField: 'isDeposit',
            title: 'Type',
            formatter: (data) => (data ? 'Deposit' : 'Withdrawal'),
        }),
        getTableColumn({
            dataField: 'status',
            formatter: (data) => <StatusBadge status={STATUSES[data as keyof typeof STATUSES]} />,
            headerFormatter: () => (
                <Select
                    currentValue={paymentRequestsType}
                    options={statusOptions || []}
                    dropdownPlacement='bottom-right'
                    placeholder='Status'
                    multiple
                    nonInputView
                    withClearButton
                    onSelect={setPaymentRequestsType}
                />
            ),
        }),
    ];
}
